import moment from "moment";
import React, { Component, useEffect, useId, useState } from "react";
import { arrayIsEqual, number } from "../../helpers/util";
import AceEditor from "../app/AceEditor";
import DatePicker from "../app/DatePicker";
import DatePickerHeader from "../app/DatePickerHeader";
import Popover from "../app/Popover";
import {
  AsyncCreatableSelectWrapper,
  AsyncSelectWrapper,
  CreatableSelectWrapper,
  SelectWrapper,
} from "../app/SelectWrapper";
import ColorPicker from "./color-picker";
import CurrencyInput from "./currency";
import DataTableInput from "./data-table-input";
import DatePickerInput, { selectedDate } from "./datepicker-input";
import FieldLabel from "./FieldLabel";
import { FieldMessages, fieldAccessibility } from "./fields/LegacyFieldAdapter";

// Blur marks a field touched; it is not itself a value change. Passing
// `field.input.value` replayed whatever the last render closed over, so when a
// selection blurred the control before React committed, Redux Form wrote the
// pre-selection value back and silently discarded the selection. Redux Form treats an
// absent blur payload as "touched, value unchanged", which is the intended semantic.
function markBlurred(field) {
  return () => field.input.onBlur?.();
}

function onChange(field) {
  return (e) => {
    if (field.maxLength && e.target.value.length > parseInt(field.maxLength, 10)) {
      return;
    }
    field.input.onChange(e.target.value);
    field.onInputChange?.(e.target.value);
  };
}

function onSelectChange(field) {
  return (option) => {
    const val = option ? option.value : null;
    field.input.onChange(val);
    field.onInputChange?.(option);
  };
}

function onTagsChange(field) {
  return (options) => {
    const values = options || [];
    field.input.onChange(values);
    field.onInputChange?.(options);
  };
}

function onCurrencyChange(field) {
  return (val) => {
    field.input.onChange(val);
    field.onInputChange?.(val);
  };
}

function onDateChange(field) {
  return (val) => {
    const date = moment.isMoment(val) ? val.format("MM/DD/YYYY") : val;
    field.input.onChange(date);
    field.onInputChange && setImmediate(() => field.onInputChange(date));
  };
}

function onColorPickerChange(field) {
  return (value) => {
    field.input.onChange(value);
    field.onInputChange?.(value);
  };
}

function withInputAttributes(field, attributes) {
  return attributes.reduce(
    (input, attribute) => (field[attribute] ? { ...input, [attribute]: field[attribute] } : input),
    field.input,
  );
}

export function InputField(field) {
  const generatedId = useId();
  const inputId = field.id || field.input.name || generatedId;
  const accessibility = fieldAccessibility(field, inputId);
  const editHintId = field.editHint ? `${inputId}-edit-hint` : undefined;
  const describedBy = [editHintId, accessibility.inputProps["aria-describedby"]].filter(Boolean).join(" ") || undefined;
  const popover = field.popoverContent && <Popover content={field.popoverContent} />;
  const input = withInputAttributes(field, ["maxLength", "pattern", "inputMode", "max", "min"]);
  return (
    <div className={field.meta.touched && field.meta.error ? "app-validation-error" : null}>
      {!field.hideLabel && (
        <FieldLabel htmlFor={inputId} popover={popover}>
          {field.placeholder}
        </FieldLabel>
      )}
      <input
        {...input}
        {...accessibility.inputProps}
        id={inputId}
        data-visual-redact={field.visualRedact}
        data-lpignore={field["data-lpignore"]}
        data-1p-ignore={field["data-1p-ignore"]}
        className={field.className}
        placeholder={field.inputPlaceholder || field.placeholder}
        type={field.type}
        onChange={onChange(field)}
        onClick={field.onClick}
        onKeyDown={field.onKeyDown}
        autoComplete={field.autoComplete}
        readOnly={field.readOnly}
        aria-describedby={describedBy}
        disabled={field.disabled}
        ref={field.refName}
      />
      {field.editHint && (
        <span id={editHintId} className="visually-hidden">
          {field.editHint}
        </span>
      )}
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}

export function PasswordField(field) {
  const generatedId = useId();
  const inputId = field.id || field.input.name || generatedId;
  const accessibility = fieldAccessibility(field, inputId);
  const [hidden, setHidden] = useState(true);
  const toggleShow = (_e) => {
    setHidden(!hidden);
  };
  const popover = field.popoverContent && <Popover content={field.popoverContent} />;
  const input = withInputAttributes(field, ["maxLength", "pattern", "inputMode"]);
  return (
    <div className={field.meta.touched && field.meta.error ? "app-validation-error" : null}>
      {!field.hideLabel && (
        <FieldLabel htmlFor={inputId} popover={popover}>
          {field.placeholder}
        </FieldLabel>
      )}
      <div className="app-input-group">
        <div className="form-wrap">
          <input
            {...input}
            {...accessibility.inputProps}
            id={inputId}
            className={field.className}
            placeholder={field.inputPlaceholder || field.placeholder}
            type={hidden ? "password" : field.type}
            onChange={onChange(field)}
            disabled={field.disabled}
            ref={field.refName}
          />
        </div>
        <span className="app-input-group__button">
          <button onClick={toggleShow} className="app-btn waves-effect waves-light" type="button">
            <i className={`fa fa-${hidden ? "eye" : "eye-slash"}`} />
          </button>
        </span>
      </div>
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}

export { default as ToggleField } from "./fields/SwitchField";

export function Hidden(field) {
  const generatedId = useId();
  const inputId = field.id || field.input.name || generatedId;
  const accessibility = fieldAccessibility(field, inputId);
  return (
    <div className={field.meta.touched && field.meta.error ? "app-validation-error" : null}>
      <input {...field.input} type="hidden" onChange={onChange(field)} />
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}

export function Alert(field) {
  return (
    <div className={field.meta.touched && field.meta.error ? "app-validation-error" : null}>
      <input {...field.input} type="hidden" onChange={onChange(field)} />
      {field.meta.touched && field.meta.error && <div className="alert alert-danger">{field.meta.error}</div>}
    </div>
  );
}

export class CurrencyField extends Component {
  render() {
    const field = this.props;
    const inputId = field.id || field.input.name;
    const accessibility = fieldAccessibility(field, inputId);
    const popover = field.popoverContent && <Popover content={field.popoverContent} />;
    if (field.input.value === 0) {
      field.input.value = "0.00";
    }
    return (
      <div className={field.meta.touched && field.meta.error ? "app-validation-error" : null}>
        {!field.hideLabel && (
          <FieldLabel htmlFor={inputId} popover={popover}>
            {field.placeholder}
          </FieldLabel>
        )}
        <CurrencyInput
          {...field.input}
          {...accessibility.inputProps}
          id={inputId}
          value={field.input.value}
          className={field.className}
          placeholder={field.inputPlaceholder || field.placeholder}
          allowBlank={field.allowBlank}
          type={field.type}
          onChange={onCurrencyChange(field)}
          disabled={field.disabled}
        />
        <FieldMessages accessibility={accessibility} field={field} />
      </div>
    );
  }
}

export function DataTableField(field) {
  const generatedId = useId();
  const inputId = field.id || field.input.name || generatedId;
  const accessibility = fieldAccessibility(field, inputId);
  return (
    <div className={field.meta.touched && field.meta.error ? "app-validation-error" : null}>
      {!field.hideLabel && <span className="app-form-label">{field.placeholder}</span>}
      <DataTableInput
        {...field.input}
        className={field.className}
        disabled={field.disabled}
        mutualFunds={field.mutualFunds}
        hideShares={field.hideShares}
        onChange={(val) => {
          field.input.onChange(val);
          field.onInputChange?.(val);
        }}
      />
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}

export { default as DropzoneInput } from "./fields/DropzoneField";

export { default as PercentageField } from "./fields/PercentageField";

export function TextField(field) {
  const generatedId = useId();
  const inputId = field.id || field.input.name || generatedId;
  const accessibility = fieldAccessibility(field, inputId);
  const popover = field.popoverContent && <Popover content={field.popoverContent} placement={field.placement} />;
  return (
    <div className={field.meta.touched && field.meta.error ? "app-validation-error" : null}>
      {!field.hideLabel && (
        <FieldLabel htmlFor={inputId} popover={popover}>
          {field.placeholder}
        </FieldLabel>
      )}
      <textarea
        {...field.input}
        {...accessibility.inputProps}
        id={inputId}
        className={field.className}
        placeholder={field.inputPlaceholder || field.placeholder}
        rows={field.rows}
        onChange={onChange(field)}
        style={field.style}
        readOnly={field.readOnly}
        onPaste={field.onPaste}
        onKeyPress={field.onKeyPress}
        disabled={field.disabled}
      />
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}

export function CodeField(field) {
  const generatedId = useId();
  const inputId = field.id || field.input.name || generatedId;
  const accessibility = fieldAccessibility(field, inputId);
  const handleLoad = (editor) => {
    editor.textInput.getElement().id = inputId;
    field.onLoad?.(editor);
  };
  return (
    <div className={field.meta.touched && field.meta.error ? "app-validation-error" : null}>
      {!field.hideLabel && (
        <label className="app-form-label" htmlFor={inputId}>
          {field.placeholder}
        </label>
      )}
      <AceEditor
        {...field.input}
        ariaLabel={field.placeholder}
        name={`${inputId}-editor`}
        onLoad={handleLoad}
        readOnly={field.readOnly}
        height={field.height || "500px"}
        width="100%"
        mode={field.mode || "javascript"}
        theme="github"
        onBlur={markBlurred(field)}
      />
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}

export { Checkbox, MultiCheckbox } from "./fields/CheckboxField";

export function DatePickerField(field) {
  const generatedId = useId();
  const inputId = field.id || field.input.name || generatedId;
  const accessibility = fieldAccessibility(field, inputId);
  const {
    minDate,
    maxDate,
    input: { value, ...inputWithoutValue },
  } = field;
  const hasSelectedDate = Boolean(value && (moment.isMoment(value) || typeof value === "string"));
  const input = {
    ...(hasSelectedDate ? inputWithoutValue : field.input),
    ...(minDate ? { minDate } : {}),
    ...(maxDate ? { maxDate } : {}),
    ...(hasSelectedDate ? { selected: selectedDate(value) } : {}),
    ...(field.openToDate ? { openToDate: field.openToDate } : {}),
  };
  return (
    <div className={field.meta.touched && field.meta.error ? "app-validation-error" : null}>
      {!field.hideLabel && (
        <label className="app-form-label" htmlFor={inputId}>
          {field.placeholder}
        </label>
      )}
      <div>
        <DatePicker
          {...input}
          {...accessibility.inputProps}
          id={inputId}
          className={`${field.className} datepicker`}
          onChange={onDateChange(field)}
          disabled={field.disabled}
          placeholder={field.placeholder}
          dateFormat="MM/DD/YYYY"
          peekNextMonth
          showMonthDropdown
          showYearDropdown
          scrollableYearDropdown
          dropdownMode="select"
          customInput={<DatePickerInput minDate={minDate} maxDate={maxDate} />}
          renderCustomHeader={(props) => <DatePickerHeader {...props} minDate={minDate} maxDate={maxDate} />}
        />
      </div>
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}

const fmt = (v) => `0${v}`.slice(-2);
const getOpts = (l) =>
  Array(l)
    .fill(0)
    .map((_, i) => {
      const value = fmt(i);
      return { label: value, value };
    });

const hourOpts = getOpts(24);
const minuteOpts = getOpts(60);

export function TimePickerField(field) {
  const generatedId = useId();
  const inputId = field.id || field.input.name || generatedId;
  const accessibility = fieldAccessibility(field, inputId);
  const [value, setValue] = useState(field.input.value);
  const [h, m] = value ? value.split(":") : [0, 0];
  const [hour, setHour] = useState(h ? hourOpts.find((o) => o.value === h) : null);
  const [minute, setMinute] = useState(m ? minuteOpts.find((o) => o.value === m) : null);
  const handleChange = (h, m) => {
    setHour(h);
    setMinute(m);
    const v = h && m ? `${h.value}:${m.value}` : null;
    setValue(v);
    field.input.onChange(v);
  };
  const handleHour = (o) => handleChange(o, minute);
  const handleMinute = (o) => handleChange(hour, o);
  // oxlint-disable-next-line react/exhaustive-deps -- Disabling the field intentionally resynchronizes cleared local picker state.
  useEffect(() => {
    if (!field.input.value) {
      setValue(null);
      setHour(null);
      setMinute(null);
    }
  }, [field.input.value, field.disabled]);
  return (
    <div className={field.meta.touched && field.meta.error ? "app-validation-error" : null}>
      {!field.hideLabel && <span className="app-form-label">{field.placeholder}</span>}
      <fieldset className="app-layout-row" aria-label={field.placeholder}>
        <div className="app-col-sm-1">
          <SelectWrapper
            placeholder="Hour"
            options={hourOpts}
            value={hour}
            onChange={handleHour}
            disabled={field.disabled}
          />
        </div>
        <div className="app-col-sm-1">
          <SelectWrapper
            placeholder="Minute"
            options={minuteOpts}
            value={minute}
            onChange={handleMinute}
            disabled={field.disabled}
          />
        </div>
      </fieldset>
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}

class SelectFieldComponent extends Component {
  render() {
    const field = this.props;
    const inputId = field.id || field.generatedId;
    const accessibility = fieldAccessibility(field, inputId);
    const labelId = `${inputId}-label`;
    const labelKey = field.labelKey || "name";
    const valueKey = field.valueKey || "id";
    const popover = field.popoverContent && <Popover content={field.popoverContent} width={field.popoverWidth} />;
    return (
      <div className={field.meta.touched && field.meta.error ? "app-validation-error" : null}>
        {!field.hideLabel && (
          <FieldLabel htmlFor={inputId} id={labelId} popover={popover}>
            {field.placeholder}
          </FieldLabel>
        )}
        <SelectWrapper
          {...field.input}
          {...accessibility.inputProps}
          id={inputId}
          aria-labelledby={!field.hideLabel ? labelId : undefined}
          visualRedact={field.visualRedact}
          placeholder={field.prompt === false ? null : field.placeholder}
          className={field.className}
          onChange={onSelectChange(field)}
          searchable={field.searchable !== undefined ? field.searchable : true}
          options={field.options.map((o) => ({
            value: o[valueKey],
            label: o[labelKey],
            disabled: o.disabled,
          }))}
          onBlur={() => {
            if (field.disableBlur) {
              return;
            }
            field.input.onBlur();
          }}
          filterOption={field.input.filterOption}
          disabled={field.disabled}
          ref={field.refName}
          clearable={field.clearable || false}
        />
        <FieldMessages accessibility={accessibility} field={field} />
      </div>
    );
  }
}

export function SelectField(field) {
  const generatedId = useId();
  return <SelectFieldComponent {...field} generatedId={generatedId} />;
}

export function TagsFields(field) {
  const generatedId = useId();
  const inputId = field.id || field.input.name || generatedId;
  const accessibility = fieldAccessibility(field, inputId);
  return (
    <div className={field.meta.touched && field.meta.error ? "app-validation-error" : null}>
      {!field.hideLabel && (
        <label className="app-form-label" htmlFor={inputId}>
          {field.placeholder}
        </label>
      )}
      <CreatableSelectWrapper
        {...field.input}
        id={inputId}
        multi={field.multi || true}
        value={field.input.value}
        placeholder={field.prompt === false ? null : field.placeholder}
        className={field.className}
        onChange={onTagsChange(field)}
        searchable={field.searchable !== undefined ? field.searchable : true}
        onBlur={markBlurred(field)}
        ref={field.refName}
        clearable={field.clearable || false}
        disabled={field.disabled}
      />
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}

export function CreatableSelect(field) {
  const generatedId = useId();
  const inputId = field.id || field.input.name || generatedId;
  const accessibility = fieldAccessibility(field, inputId);
  return (
    <div className={field.meta.touched && field.meta.error ? "app-validation-error" : null}>
      {!field.hideLabel && (
        <label className="app-form-label" htmlFor={inputId}>
          {field.placeholder}
        </label>
      )}
      <CreatableSelectWrapper
        {...field.input}
        id={inputId}
        multi={false}
        value={field.input.value}
        options={field.options}
        placeholder={field.prompt === false ? null : field.placeholder}
        className={field.className}
        onChange={onSelectChange(field)}
        searchable={field.searchable !== undefined ? field.searchable : true}
        onBlur={markBlurred(field)}
        ref={field.refName}
        clearable={field.clearable || false}
        disabled={field.disabled}
      />
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}

export function AsyncSelect(field) {
  const generatedId = useId();
  const inputId = field.id || generatedId;
  const accessibility = fieldAccessibility(field, inputId);
  const labelId = `${inputId}-label`;
  const popover = field.popoverContent && <Popover content={field.popoverContent} width={field.popoverWidth} />;
  return (
    <div className={field.meta.touched && field.meta.error ? "app-validation-error" : null}>
      {!field.hideLabel && (
        <FieldLabel htmlFor={inputId} id={labelId} popover={popover}>
          {field.placeholder}
        </FieldLabel>
      )}
      <AsyncSelectWrapper
        {...field.input}
        {...accessibility.inputProps}
        id={inputId}
        aria-labelledby={!field.hideLabel ? labelId : undefined}
        placeholder={field.prompt === false ? null : field.placeholder}
        className={field.className}
        onChange={onSelectChange(field)}
        disabled={field.disabled}
        loadOptions={field.loadOptions}
        cache={false}
        ref={field.refName}
        onBlur={markBlurred(field)}
      />
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}

export function AsyncCreatableSelect(field) {
  const generatedId = useId();
  const inputId = field.id || field.input.name || generatedId;
  const accessibility = fieldAccessibility(field, inputId);
  const loadOptions = (input) =>
    Promise.resolve(field.loadOptions(input))
      .then((result) => {
        if (field.onOptionsLoaded) {
          field.onOptionsLoaded(result, input);
        }
        return result;
      })
      .catch((error) => {
        if (field.onLoadError) {
          field.onLoadError(error, input);
        }
        return { options: [] };
      });
  const handleNewOptionClick = (input) => {
    const rawInput =
      typeof input === "string" ? input : input && typeof input === "object" ? input.label || input.value || "" : "";
    const trimmed = rawInput.trim();
    if (!trimmed) {
      return;
    }
    const newOption = { value: trimmed, label: trimmed };
    field.input.onChange(newOption.value);
    if (field.onInputChange) {
      field.onInputChange(newOption);
    }
    if (field.onNewOptionClick) {
      field.onNewOptionClick(newOption);
    }
    // Close menu after create using both redux-form and active element blur.
    setTimeout(() => {
      field.input.onBlur(newOption.value);
      if (typeof document !== "undefined" && document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }
    }, 0);
  };

  return (
    <div className={field.meta.touched && field.meta.error ? "app-validation-error" : null}>
      {!field.hideLabel && (
        <label className="app-form-label" htmlFor={inputId}>
          {field.placeholder}
        </label>
      )}
      <AsyncCreatableSelectWrapper
        {...field.input}
        id={inputId}
        placeholder={field.prompt === false ? null : field.placeholder}
        className={field.className}
        onChange={onSelectChange(field)}
        disabled={field.disabled}
        loadOptions={loadOptions}
        cache={false}
        ref={field.refName}
        onBlur={markBlurred(field)}
        onNewOptionClick={handleNewOptionClick}
        onInputChange={(input) => (field.maxLength ? (input || "").slice(0, field.maxLength) : input)}
      />
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}

let asyncSelectFieldSequence = 0;

export class AsyncSelectField extends Component {
  constructor(props) {
    super(props);
    asyncSelectFieldSequence += 1;
    this.generatedId = `async-select-${asyncSelectFieldSequence}`;
    this.state = {
      loading: true,
      options: [],
    };
    this.loadOptions = props.loadParams ? props.loadOptions(...props.loadParams) : props.loadOptions;
  }

  componentDidMount() {
    this._isMounted = true;
    this.load();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.loadRequest += 1;
  }

  componentDidUpdate(prevProps) {
    let reloadOptions = false;
    this.loadOptions = this.props.loadOptions;
    if (this.props.loadParams) {
      this.loadOptions = this.props.loadOptions(...this.props.loadParams);
      reloadOptions = !arrayIsEqual(prevProps.loadParams, this.props.loadParams);
    }
    if (reloadOptions || (this.props.reloadOptionsOnChange && prevProps.input.value !== this.props.input.value)) {
      this.load("");
    }
  }

  loadRequest = 0;

  load = (value) => {
    const request = ++this.loadRequest;
    return this.loadOptions(value).then((result) => {
      if (this._isMounted && request === this.loadRequest) {
        this.setState({ loading: false, options: result.options });
      }
    });
  };

  onInputChange = (value) => {
    if (value !== "") {
      this.load(value);
    }
    return value;
  };

  onChange = (option) => {
    const field = this.props;
    const value = option ? option.value : null;
    field.input.onChange(value);
    field.onInputChange?.(option);
    if (value === null) {
      this.load("");
    }
  };

  render() {
    const field = this.props;
    const inputId = field.id || this.generatedId;
    const accessibility = fieldAccessibility(field, inputId);
    const labelId = `${inputId}-label`;
    const { options, loading } = this.state;
    const placeholder = field.prompt === false ? null : field.placeholder;
    const popover = field.popoverContent && <Popover content={field.popoverContent} />;
    return (
      <div className={field.meta.touched && field.meta.error ? "app-validation-error" : null}>
        {!field.hideLabel && (
          <FieldLabel htmlFor={inputId} id={labelId} popover={popover}>
            {field.placeholder}
          </FieldLabel>
        )}
        <SelectWrapper
          {...field.input}
          {...accessibility.inputProps}
          id={inputId}
          aria-labelledby={!field.hideLabel ? labelId : undefined}
          placeholder={placeholder}
          className={field.className}
          options={options}
          isLoading={loading}
          disabled={field.disabled}
          ref={field.refName}
          cache={false}
          onChange={this.onChange}
          onInputChange={this.onInputChange}
          onBlur={markBlurred(field)}
        />
        <FieldMessages accessibility={accessibility} field={field} />
      </div>
    );
  }
}

// Extracted while removing react-filtered-multiselect. Re-exported under its
// historical name so the four `Inputs.AsyncMultiSelectField` call sites are
// unchanged. See docs/multiselect-migration-audit.md.
export { default as AsyncMultiSelectField } from "./fields/MultiSelectField";

export function ColorPickerField(field) {
  const generatedId = useId();
  const labelId = field.labelId || `${field.input.name || generatedId}-label`;
  const inputId = field.id || field.input.name || generatedId;
  const accessibility = fieldAccessibility(field, inputId);
  return (
    <div className={field.meta.touched && field.meta.error ? "app-validation-error" : null}>
      {!field.hideLabel && (
        <span className="app-form-label" id={labelId}>
          {field.placeholder}
        </span>
      )}
      <ColorPicker
        aria-labelledby={!field.hideLabel ? labelId : undefined}
        value={field.input.value}
        onChange={onColorPickerChange(field)}
      />
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}

// Redux Form 8 re-registers a field whenever its `validate` prop stops being
// deeply equal to the previous one, so a factory that returns a new function on
// every render drives a register/unregister loop. Memoizing by argument keeps the
// returned validator identity stable.
//
// Keys are tagged by type so that values which share a string form (`1` and
// `"1"`, `null` and `"null"`) cannot collide. Arguments that have no stable
// primitive key bypass the cache instead of collapsing onto one entry; no
// callsite passes one today, and returning an uncached validator only restores
// the unmemoized behaviour for it.
const VALIDATOR_CACHE_LIMIT = 128;

function validatorCacheKey(args) {
  const parts = [];
  for (const arg of args) {
    if (arg === null || arg === undefined || ["string", "number", "boolean"].includes(typeof arg)) {
      parts.push(`${typeof arg}:${String(arg)}`);
    } else {
      return null;
    }
  }
  return parts.join("|");
}

function memoizeValidatorFactory(factory) {
  const validators = new Map();
  return (...args) => {
    const key = validatorCacheKey(args);
    if (key === null) {
      return factory(...args);
    }
    if (validators.has(key)) {
      // Refresh recency so a still-mounted field cannot be FIFO-evicted by
      // churn in another field (billing tiers key on edited values).
      const cached = validators.get(key);
      validators.delete(key);
      validators.set(key, cached);
      return cached;
    }
    // Bounded because callsites may key on edited field values. Map is
    // insertion-ordered; after the hit refresh above, the oldest entry is the
    // least recently used.
    if (validators.size >= VALIDATOR_CACHE_LIMIT) {
      validators.delete(validators.keys().next().value);
    }
    const created = factory(...args);
    validators.set(key, created);
    return created;
  };
}

export const largerThan = memoizeValidatorFactory(
  (min, message) => (value) => (min > number(value) ? message : undefined),
);

export const lessThan = memoizeValidatorFactory(
  (max, message) => (value) => (number(value) > max ? message || `Maximum value is ${max}` : undefined),
);

export const requiredIf = memoizeValidatorFactory(
  (isRequired) => (value) => (isRequired ? (value ? undefined : "Required") : undefined),
);

export const maxLength = memoizeValidatorFactory(
  (max) => (value) => (value && value.length > max ? `Must be ${max} characters` : undefined),
);

export const minLength = memoizeValidatorFactory(
  (min) => (value) => (value && value.length < min ? `Must be ${min} characters` : undefined),
);

export function email(value) {
  return value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value) ? "Invalid email address" : undefined;
}

export function numbers(value) {
  if (!value) {
    return value;
  }
  const onlyNums = value.replace(/[^\d]/g, "");
  return onlyNums;
}

export function phone(value) {
  if (!value) {
    return value;
  }
  const onlyNums = value.replace(/[^\d]/g, "");
  if (onlyNums.length <= 3) {
    return onlyNums;
  }
  if (onlyNums.length <= 7) {
    return `(${onlyNums.slice(0, 3)}) ${onlyNums.slice(3)}`;
  }
  return `(${onlyNums.slice(0, 3)}) ${onlyNums.slice(3, 6)}-${onlyNums.slice(6, 10)}`;
}
